import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { createPlatformEmployee, updatePlatformEmployee } from "@/services/api";

const employeeRoles = ["PLATFORM_ADMIN", "MANAGER", "SALES_EMPLOYEE", "INSTALLER", "TECHNICIAN", "SUPPORT"];
const employeeStatuses = ["ACTIVE", "INACTIVE", "SUSPENDED", "REVOKED"];
const emptyDraft = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  employee_number: "",
  role: "INSTALLER",
  status: "ACTIVE",
  assigned_region: "",
  assigned_customer_ids: [],
  assigned_installation_ids: [],
};

export const AdminEmployeesPanel = ({ customers = [], employees = [], installations = [], token, onRefresh }) => {
  const [draft, setDraft] = useState(emptyDraft);
  const [feedback, setFeedback] = useState("");
  const [setupLink, setSetupLink] = useState("");
  const [pending, setPending] = useState("");
  const [roleDrafts, setRoleDrafts] = useState({});
  const [statusDrafts, setStatusDrafts] = useState({});

  const selectedCustomerInstallations = useMemo(
    () => installations.filter((item) => draft.assigned_customer_ids.includes(item.tenant_id)),
    [draft.assigned_customer_ids, installations],
  );

  const toggleScope = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((item) => item !== value) : [...prev[key], value],
    }));
  };

  const runAction = async (key, callback, successMessage) => {
    setPending(key);
    setFeedback("");
    try {
      await callback();
      setFeedback(successMessage);
      await onRefresh?.();
    } catch (error) {
      setFeedback(error.message || "Employee action failed.");
    } finally {
      setPending("");
    }
  };

  return (
    <div className="aion-admin-stack" data-testid="admin-employees-panel">
      <AionSectionCard title="Employees" eyebrow="OPERATOR ADMIN" subtitle="AION employees stay in the existing user system with scoped customer and installation assignments." testId="admin-employees-card">
        {feedback ? <div className="aion-note" data-testid="admin-employees-feedback">{feedback}</div> : null}
        {setupLink ? <div className="aion-note" data-testid="admin-employees-setup-link-box">Secure setup link preview: {setupLink}</div> : null}
        <div className="aion-settings-grid" data-testid="admin-employees-form-grid">
          <label className="aion-settings-field" data-testid="admin-employees-first-name-field"><span>First name</span><Input value={draft.first_name} onChange={(event) => setDraft((prev) => ({ ...prev, first_name: event.target.value }))} data-testid="admin-employees-first-name-input" /></label>
          <label className="aion-settings-field" data-testid="admin-employees-last-name-field"><span>Last name</span><Input value={draft.last_name} onChange={(event) => setDraft((prev) => ({ ...prev, last_name: event.target.value }))} data-testid="admin-employees-last-name-input" /></label>
          <label className="aion-settings-field" data-testid="admin-employees-email-field"><span>Email</span><Input value={draft.email} onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))} data-testid="admin-employees-email-input" /></label>
          <div className="aion-note" data-testid="admin-employees-password-note">Passwords are created by the employee during secure onboarding. No shared admin password is exposed.</div>
          <label className="aion-settings-field" data-testid="admin-employees-phone-field"><span>Phone</span><Input value={draft.phone} onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))} data-testid="admin-employees-phone-input" /></label>
          <label className="aion-settings-field" data-testid="admin-employees-number-field"><span>Employee number</span><Input value={draft.employee_number} onChange={(event) => setDraft((prev) => ({ ...prev, employee_number: event.target.value }))} data-testid="admin-employees-number-input" /></label>
          <label className="aion-settings-field" data-testid="admin-employees-role-field"><span>Role</span><Select value={draft.role} onValueChange={(value) => setDraft((prev) => ({ ...prev, role: value }))}><SelectTrigger data-testid="admin-employees-role-select"><SelectValue /></SelectTrigger><SelectContent>{employeeRoles.map((role) => <SelectItem key={role} value={role} data-testid={`admin-employees-role-option-${role.toLowerCase()}`}>{role}</SelectItem>)}</SelectContent></Select></label>
          <label className="aion-settings-field" data-testid="admin-employees-status-field"><span>Status</span><Select value={draft.status} onValueChange={(value) => setDraft((prev) => ({ ...prev, status: value }))}><SelectTrigger data-testid="admin-employees-status-select"><SelectValue /></SelectTrigger><SelectContent>{employeeStatuses.map((status) => <SelectItem key={status} value={status} data-testid={`admin-employees-status-option-${status.toLowerCase()}`}>{status}</SelectItem>)}</SelectContent></Select></label>
          <label className="aion-settings-field" data-testid="admin-employees-region-field"><span>Assigned region</span><Input value={draft.assigned_region} onChange={(event) => setDraft((prev) => ({ ...prev, assigned_region: event.target.value }))} data-testid="admin-employees-region-input" /></label>
        </div>

        <div className="aion-admin-layout" data-testid="admin-employees-scope-layout">
          <div className="aion-list" data-testid="admin-employees-customer-scope-list">
            {customers.map((customer) => {
              const active = draft.assigned_customer_ids.includes(customer.tenant_id);
              return (
                <button type="button" key={customer.tenant_id} className={`aion-list-row${active ? " aion-memory-card-active" : ""}`} onClick={() => toggleScope("assigned_customer_ids", customer.tenant_id)} data-testid={`admin-employees-customer-scope-${customer.tenant_id}`}>
                  <div className="aion-list-copy">
                    <strong>{customer.company_name || customer.name}</strong>
                    <small>{active ? "Assigned" : "Tap to assign customer scope"}</small>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="aion-list" data-testid="admin-employees-installation-scope-list">
            {selectedCustomerInstallations.map((installation) => {
              const installationId = installation.installation_id || installation.id;
              const active = draft.assigned_installation_ids.includes(installationId);
              return (
                <button type="button" key={installationId} className={`aion-list-row${active ? " aion-memory-card-active" : ""}`} onClick={() => toggleScope("assigned_installation_ids", installationId)} data-testid={`admin-employees-installation-scope-${installationId}`}>
                  <div className="aion-list-copy">
                    <strong>{installation.name}</strong>
                    <small>{active ? "Installation assigned" : "Tap to scope this installation"}</small>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="aion-inline-actions" data-testid="admin-employees-actions">
          <Button disabled={pending === "create-employee"} onClick={() => runAction("create-employee", async () => {
            const created = await createPlatformEmployee({ ...draft, assigned_project_ids: draft.assigned_installation_ids }, token);
            setSetupLink(created.setup_link_preview || "");
            setDraft(emptyDraft);
          }, "Employee created.")} data-testid="admin-employees-create-button">{pending === "create-employee" ? "Creating…" : "+ Neuer Mitarbeiter / Create Employee"}</Button>
        </div>
      </AionSectionCard>

      <AionSectionCard title="Existing employee roster" eyebrow="SCOPES" subtitle="Role, status, and assignments stay editable without creating a second employee auth system." testId="admin-employees-roster-card">
        <div className="aion-list" data-testid="admin-employees-roster-list">
          {employees.map((employee) => {
            const profile = employee.employee_profile || {};
            return (
              <div className="aion-list-row" key={employee.id} data-testid={`admin-employees-roster-item-${profile.employee_id || employee.id}`}>
                <div className="aion-list-copy">
                  <strong>{employee.name}</strong>
                  <small>{employee.email} · {profile.employee_number || "—"} · {profile.assigned_region || "No region"}</small>
                  <small>{(profile.assigned_customer_ids || []).join(", ") || "No customer scope"} · {(profile.assigned_installation_ids || []).join(", ") || "No installation scope"}</small>
                </div>
                <div className="aion-admin-inline-grid" data-testid={`admin-employees-roster-actions-${profile.employee_id || employee.id}`}>
                  <Select value={roleDrafts[employee.id] || profile.role || employee.account_role || "INSTALLER"} onValueChange={(value) => setRoleDrafts((prev) => ({ ...prev, [employee.id]: value }))}>
                    <SelectTrigger data-testid={`admin-employees-roster-role-${profile.employee_id || employee.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{employeeRoles.map((role) => <SelectItem key={role} value={role} data-testid={`admin-employees-roster-role-option-${profile.employee_id || employee.id}-${role.toLowerCase()}`}>{role}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={statusDrafts[employee.id] || profile.status || employee.account_status || "ACTIVE"} onValueChange={(value) => setStatusDrafts((prev) => ({ ...prev, [employee.id]: value }))}>
                    <SelectTrigger data-testid={`admin-employees-roster-status-${profile.employee_id || employee.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{employeeStatuses.map((status) => <SelectItem key={status} value={status} data-testid={`admin-employees-roster-status-option-${profile.employee_id || employee.id}-${status.toLowerCase()}`}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" disabled={pending === `employee-${employee.id}`} onClick={() => runAction(`employee-${employee.id}`, () => updatePlatformEmployee(profile.employee_id, { role: roleDrafts[employee.id] || profile.role, status: statusDrafts[employee.id] || profile.status }, token), "Employee updated.")} data-testid={`admin-employees-roster-save-${profile.employee_id || employee.id}`}>Save</Button>
                </div>
              </div>
            );
          })}
        </div>
      </AionSectionCard>
    </div>
  );
};